import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SkeletonBlock, Typography } from '@one-thd/sui-atomic-components';
import {
  useLazyDataModel,
  extend,
  params,
  arrayOf,
  number as numberType,
  string as stringType,
  customType
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';

export const GroupSubTitle = ({
  groupId,
  products,
  onGroupTotalAvailable,
  totalItems,
  isLoading,
  isMobile,
  isDefault
}) => {
  const [groupTotal, setGroupTotal] = useState(0);
  const [previouslyFetchedProducts, setPreviouslyFetchedProducts] = useState([]);
  // Prefer totalItems passed from parent (which includes intents/resolved items)
  const numberOfItems = typeof totalItems === 'number' ? totalItems : (products?.length || 0);
  const isFirstLoading = useRef(true);
  const { storeId } = useStore();
  const [subtotalError, setSubtotalError] = useState(false); // Convert from useRef to useState
  const isMountedRef = useRef(true); // Add ref to track mount status
  const hasReportedZero = useRef(false); // Track if we've already reported zero to prevent loops

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const catalogProducts = useMemo(() => {
    return products.filter((product) => product.type === 'catalog');
  }, [products]);

  const [fetchSubTotal, subTotalResponse] = useLazyDataModel('priceSubtotal', {
    variables: {
      storeId
    },
    context: { withAuth: true }
  });

  const { loading: subtotalLoading } = subTotalResponse || {};

  // This will avoid refetching subtotal on rearranging products in same group
  const isAlreadyFetched = (newProducts) => {
    if (previouslyFetchedProducts.length === newProducts.length) {
      let isFetched = true;
      newProducts.forEach((product) => {
        if (isFetched) {
          const currentProduct = previouslyFetchedProducts.find(
            (prevProduct) => prevProduct.itemId === product.itemId
          );
          if (!currentProduct) isFetched = false;
          if (currentProduct?.quantity !== product.quantity) {
            isFetched = false;
          }
        }
      });
      return isFetched;
    }
    return false;
  };

  useEffect(() => {
    const fetchTotal = async () => {
      const lineItems = catalogProducts.map((product) => (
        { itemId: product.itemId, quantity: product.quantity }
      )).filter((item) => item.itemId);
      if (lineItems?.length) {
        await fetchSubTotal({ variables: { lineItems } });
      }
      isFirstLoading.current = false;
      setPreviouslyFetchedProducts(products);
      hasReportedZero.current = false; // Reset the zero reporting flag when we have products
    };

    if (products?.length) {
      if (!isAlreadyFetched(products)) {
        fetchTotal();
      }
    } else {
      // When products become empty, immediately reset to 0 and report to parent (only once)
      setPreviouslyFetchedProducts([]);
      setGroupTotal(0);

      // Only report zero once to prevent infinite loops
      if (isMountedRef.current && !hasReportedZero.current) {
        hasReportedZero.current = true;
        onGroupTotalAvailable(groupId, 0);
      }
    }
  }, [products]);

  useEffect(() => {
    const { data, error } = subTotalResponse || {};
    if (isMountedRef.current) {
      if (data && !error) {
        setSubtotalError(false);
        setGroupTotal(data?.priceSubtotal?.subtotal || 0);
        hasReportedZero.current = false; // Reset flag when we get a valid subtotal
        // Directly report the subtotal when we get it
        onGroupTotalAvailable(groupId, data?.priceSubtotal?.subtotal || 0);
      }
      if (error) {
        setSubtotalError(true);
      }
    }
  }, [subTotalResponse]);

  // Remove the problematic useEffect that was causing infinite loops
  // The reporting is now handled directly in the subTotalResponse useEffect above

  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return '---.--';
    }
    return price.toFixed(2);
  };

  // Hide subtitle for default/ungrouped items but still report totals (AC1 - PRO41-1058)
  if (isDefault) {
    return null;
  }

  if (isLoading || subtotalLoading) {
    return (
      <div className="sui-flex sui-my-1 sui-w-80">
        <SkeletonBlock aspect="wide" height={6} />
      </div>
    );
  }

  return (
    <div className="sui-flex sui-gap-1 sui-my-1">
      <Typography variant="body-base" color="subtle">
        {numberOfItems || 0} Items | Estimated Subtotal:&nbsp;
      </Typography>
      <Typography variant="body-base" color="subtle">
        {subtotalError ? '$---.--' : `$${formatPrice(groupTotal)}`}
      </Typography>
    </div>
  );
};

GroupSubTitle.displayName = 'GroupSubTitle';

GroupSubTitle.propTypes = {
  groupId: PropTypes.string.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    type: PropTypes.string
  })).isRequired,
  onGroupTotalAvailable: PropTypes.func.isRequired,
  totalItems: PropTypes.number,
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  isDefault: PropTypes.bool
};

GroupSubTitle.defaultProps = {
  isLoading: false,
  isMobile: false,
  totalItems: null,
  isDefault: false
};

// Initialize dataModel with priceSubtotal like project-details
GroupSubTitle.dataModel = extend({
  priceSubtotal: params({
    lineItems: arrayOf(customType('LineItem').shape({
      itemId: stringType(),
      quantity: numberType()
    })).isRequired(),
    storeId: stringType().isRequired()
  }).shape({
    subtotal: numberType()
  })
});
